import React, { useEffect } from 'react';
import { LOCAL_MODELS, GEMINI_MODELS, MINIMAX_MODELS } from '../utils/localModels';

/**
 * Shared OCR/grading model + host selectors for two-step local checking.
 * Used on Checking (AssessmentTab) and Rechecks (RechecksTab).
 */
export default function TwoStepAiControls({
  ocrModel,
  setOcrModel,
  gradingModel,
  setGradingModel,
  ocrHost,
  setOcrHost,
  gradingHost,
  setGradingHost,
  hosts = [],
  isLocalProvider = true,
  isGeminiProvider = false,
  isMinimaxProvider = false,
  title = 'AI models & hosts',
  showHosts = true,
  combined = false,
  quiet = false
}) {
  useEffect(() => {
    if (!Array.isArray(hosts) || hosts.length === 0) return;
    const keys = new Set(hosts.map((h) => h.key));
    if (!ocrHost || !keys.has(ocrHost)) {
      if (keys.has('gpu')) setOcrHost('gpu');
      else setOcrHost(hosts[0].key);
    }
    if (!gradingHost || !keys.has(gradingHost)) {
      if (keys.has('mac')) setGradingHost('mac');
      else setGradingHost(hosts[Math.min(1, hosts.length - 1)].key);
    }
  }, [hosts]); // eslint-disable-line react-hooks/exhaustive-deps

  const labelStyle = quiet
    ? {
      display: 'block',
      fontSize: 11,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'var(--text-secondary, #6b7280)',
      fontWeight: 600,
      marginBottom: 4
    }
    : {
      display: 'block',
      fontSize: 11,
      color: 'var(--two-step-purple, #6D28D9)',
      fontWeight: 700,
      marginBottom: 2
    };
  const selectStyle = {
    width: '100%',
    height: quiet ? 32 : undefined,
    padding: quiet ? '0 8px' : '5px 8px',
    border: quiet ? '1px solid var(--border-color, #d1d5db)' : '1px solid rgba(124, 58, 237, 0.35)',
    borderRadius: 6,
    background: 'var(--select-bg, #fff)',
    color: 'var(--text-primary, #111827)',
    fontSize: quiet ? 13 : 12,
    minWidth: 0
  };
  const cellStyle = { flex: '1 1 0', minWidth: 110 };

  let providerOptions = null;
  if (combined) {
    providerOptions = (
      <>
        <optgroup label="Gemini">
          {GEMINI_MODELS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </optgroup>
        <optgroup label="Local">
          {LOCAL_MODELS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </optgroup>
      </>
    );
  } else if (isGeminiProvider) {
    providerOptions = (
      <>
        {GEMINI_MODELS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </>
    );
  } else if (isLocalProvider) {
    providerOptions = (
      <>
        <option value="">Select model</option>
        {LOCAL_MODELS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </>
    );
  } else if (isMinimaxProvider) {
    providerOptions = (
      <>
        <option value="">Use server default</option>
        {MINIMAX_MODELS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </>
    );
  }

  if (!providerOptions) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: quiet ? 8 : 6,
        padding: quiet ? '10px 12px' : '8px 10px',
        background: quiet ? 'var(--surface, #f9fafb)' : 'rgba(167, 139, 250, 0.08)',
        border: quiet ? '1px solid var(--border-color, #e5e7eb)' : '1px solid rgba(124, 58, 237, 0.25)',
        borderRadius: 8,
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: quiet ? 'var(--text-primary, #111827)' : 'var(--two-step-purple, #6D28D9)', fontWeight: 600, fontSize: 13 }}>
        <i className="fa-solid fa-sliders"></i>
        {title}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={cellStyle}>
          <label style={labelStyle}>OCR Model</label>
          <select value={ocrModel} onChange={(e) => setOcrModel(e.target.value)} style={selectStyle} title="Vision OCR model">
            {providerOptions}
          </select>
        </div>
        <div style={cellStyle}>
          <label style={labelStyle}>Grading Model</label>
          <select value={gradingModel} onChange={(e) => setGradingModel(e.target.value)} style={selectStyle} title="Text grading model">
            {providerOptions}
          </select>
        </div>
      </div>

      {showHosts && !combined && isLocalProvider && Array.isArray(hosts) && hosts.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={cellStyle}>
            <label style={labelStyle}>OCR Host</label>
            <select value={ocrHost} onChange={(e) => setOcrHost(e.target.value)} style={selectStyle} title="GPU vs Mac for OCR">
              {hosts.map((h) => (
                <option key={h.key} value={h.key}>
                  {h.label} ({String(h.url || '').replace(/^https?:\/\//, '')})
                </option>
              ))}
            </select>
          </div>
          <div style={cellStyle}>
            <label style={labelStyle}>Grading Host</label>
            <select value={gradingHost} onChange={(e) => setGradingHost(e.target.value)} style={selectStyle} title="GPU vs Mac for grading">
              {hosts.map((h) => (
                <option key={`g-${h.key}`} value={h.key}>
                  {h.label} ({String(h.url || '').replace(/^https?:\/\//, '')})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {!combined && isLocalProvider && ocrHost && gradingHost && (
        <span style={{ fontSize: 10.5, color: 'var(--two-step-purple-info, #7C3AED)', lineHeight: 1.35 }}>
          {ocrHost !== gradingHost
            ? `OCR → ${ocrHost} (1 at a time) · Grading → ${gradingHost} (max 1)`
            : `Same host (${ocrHost}) for OCR + grading`}
        </span>
      )}
    </div>
  );
}
